import { motion } from "framer-motion";
import type { RecommendationData } from "../types";

/**
 * Beyond Sure — single-plan recommendation card.
 *
 * Use case: the assistant has compared a field and picked the ONE plan
 * to recommend. Mirrors the conviction line from the brand voice —
 * "if I were you, I'd pick this." Carries plan name + 3 key stats
 * (cover / premium / claim-settlement ratio) + a one-line "why this
 * fits you" justification.
 *
 * Visual: glassy white card on a soft brand-mint plate, with the
 * "MY PICK" eyebrow + small #1 chip in the top-right. Mirrors the
 * email-template 04-recommendation pattern so the chat + the email
 * read as the same brand.
 */

interface RecommendationCardProps {
  data: RecommendationData;
}

export function RecommendationCard({ data }: RecommendationCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      data-testid="message-recommendation"
      className={[
        "rounded-[14px] p-4 sm:p-5",
        // Tasteful glass over the brand-mint plate.
        "bg-[rgb(255_255_255_/_0.86)] backdrop-blur-sm",
        "border border-[var(--color-ink-08)]",
        "shadow-[0_6px_22px_-14px_rgba(20,75,51,0.4)]",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="bsure-caps text-[10.5px] text-[var(--color-brand-deep)] font-semibold tracking-[0.16em]">
            My pick · for you
          </p>
          <h3 className="bsure-display mt-1 text-[20px] sm:text-[22px] leading-[1.2] font-bold text-[var(--color-ink)]">
            {data.planName}
          </h3>
          <p className="mt-0.5 text-[12.5px] text-[var(--color-ink-60)]">
            by {data.insurer}
          </p>
        </div>
        <span
          className="inline-flex h-7 px-2.5 items-center rounded-full text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[var(--color-paper)]"
          style={{ backgroundColor: "var(--color-brand-deep)" }}
        >
          #1
        </span>
      </div>

      {/* Stats grid */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Cover" value={data.cover} />
        <Stat label="Annual" value={data.premium} />
        <Stat label="Claim ratio" value={data.claimRatio} />
      </div>

      {/* Why this fits */}
      <div
        className={[
          "mt-4 rounded-[10px] px-3.5 py-3",
          "bg-[var(--color-brand-soft)]",
          "text-[13.5px] leading-[1.55] text-[var(--color-ink)]",
        ].join(" ")}
      >
        <span className="bsure-caps text-[10px] font-semibold tracking-[0.14em] text-[var(--color-brand-deep)] block mb-1">
          Why this fits you
        </span>
        {data.fitReason}
      </div>
    </motion.div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="bsure-caps text-[9.5px] font-semibold tracking-[0.14em] text-[var(--color-ink-40)]">
        {label}
      </p>
      <p className="mt-1 text-[15px] font-semibold tabular-nums text-[var(--color-ink)]">
        {value}
      </p>
    </div>
  );
}

export default RecommendationCard;
